import React from "react";
import "./OutletPage.css";
import { CheckCircle, ArrowRight, Play } from "lucide-react";

import heroImg    from "../image/gen-fc-hero.png";
import qrImg      from "../image/gen-fc-qr.png";
import reportImg  from "../image/gen-fc-reports.png";
import scanImg    from "../image/gen-fc-scan.png";
import userImg    from "../image/gen-fc-user.png";
import loyaltyImg from "../image/gen-fc-loyalty.png";
import demoImg    from "../image/DemoForm_img.png";

const GRADIENT = "linear-gradient(135deg, #eab308, #ca8a04)";
const GLOW     = "rgba(234,179,8,0.3)";

const css = `
  .op-foodcourt { --op-accent:#eab308; --op-gradient:${GRADIENT}; --op-glow:${GLOW};
    --op-accent-10:rgba(234,179,8,0.1); --op-accent-30:rgba(234,179,8,0.3);
    --op-accent-05:rgba(234,179,8,0.05); }
`;

export default function FoodCourtOutletPage() {
  return (
    <div className="op-page op-foodcourt">
      <style>{css}</style>

      {/* ── HERO ── */}
      <section className="op-hero">
        <div className="op-hero__bg">
          <img src={heroImg} alt="Food Court" />
          <div className="op-hero__gradient" />
          <div className="op-hero__noise" />
        </div>
        <div className="op-hero__content">
          <div className="op-hero__eyebrow">
            <span className="op-hero__eyebrow-dot" />
            Food Court &amp; Canteen POS
          </div>
          <h1 className="op-hero__title">
            Many counters,<br />
            <span className="op-accent-text">one system</span>
          </h1>
          <p className="op-hero__subtitle">
            QR code ordering, counter-wise billing, unified owner reports — KadaiPOS turns a chaotic food court into a smooth, high-throughput operation.
          </p>
          <div className="op-hero__actions">
            <button
              className="op-btn-primary"
              onClick={() => document.getElementById("op-demo")?.scrollIntoView({ behavior: "smooth" })}
            >
              Get Free Demo <ArrowRight size={18} />
            </button>
            <button className="op-btn-ghost"><Play size={16} /> Watch Demo</button>
          </div>
          <div className="op-hero__stats">
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">50+</span>
              <span className="op-hero__stat-label">Counters Managed</span>
            </div>
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">QR</span>
              <span className="op-hero__stat-label">Self-Order</span>
            </div>
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">Live</span>
              <span className="op-hero__stat-label">Counter Reports</span>
            </div>
            <div className="op-hero__stat-item">
              <span className="op-hero__stat-val">Zero</span>
              <span className="op-hero__stat-label">Queue Confusion</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── BENTO ── */}
      <section className="op-bento">
        <div className="op-bento__header">
          <div className="op-section-eyebrow">Core Features</div>
          <h2 className="op-section-title">Built for high-volume, multi-vendor spaces</h2>
          <p className="op-section-subtitle">
            Whether it's a mall food court or an office canteen — KadaiPOS keeps every counter independent yet fully connected to the owner.
          </p>
        </div>

        <div className="op-bento__grid">
          {/* Accent narrow – Counter-wise */}
          <div className="op-card op-card--narrow op-card--accent">
            <div className="op-card__body" style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <div style={{ fontSize: "3rem", marginBottom: "16px" }}>🏪</div>
              <h3 className="op-card__title" style={{ color: "#fff" }}>Counter-wise Billing</h3>
              <p className="op-card__desc" style={{ color: "rgba(255,255,255,0.75)" }}>
                Each vendor/counter runs their own independent billing. All transactions roll up into a single owner dashboard automatically.
              </p>
              <ul className="op-card__list">
                <li>Separate login per counter</li>
                <li>Counter-wise sales isolation</li>
                <li>Owner sees all counters in one view</li>
              </ul>
            </div>
          </div>

          {/* Wide – QR Ordering */}
          <div className="op-card op-card--wide">
            <img src={qrImg} alt="QR Ordering" className="op-card__img" />
            <div className="op-card__body">
              <div className="op-card__tag">📱 QR Code Ordering</div>
              <h3 className="op-card__title">Customers order from their phone — no staff needed at the table</h3>
              <p className="op-card__desc">
                Place a QR code on each table or tray. Guests scan, browse the full menu, and order. The order lands directly at the respective counter's screen.
              </p>
              <ul className="op-card__list">
                <li>QR per table → order goes to correct counter</li>
                <li>Multi-counter cart — one order from multiple vendors</li>
                <li>Real-time order status shown on customer's phone</li>
                <li>Pay online or at counter — customer's choice</li>
              </ul>
            </div>
          </div>

          {/* Half – Counter Reports */}
          <div className="op-card op-card--half">
            <img src={reportImg} alt="Counter Reports" className="op-card__img op-card__img--tall" />
            <div className="op-card__body">
              <div className="op-card__tag">📊 Counter-wise Reports</div>
              <h3 className="op-card__title">See exactly who's selling what — and how much</h3>
              <p className="op-card__desc">Compare counter performance, peak hours, bestsellers, and revenue side-by-side. Negotiate vendor commission with real data.</p>
            </div>
          </div>

          {/* Half – Scan & Order */}
          <div className="op-card op-card--half">
            <img src={scanImg} alt="Scan and Order" className="op-card__img op-card__img--tall" />
            <div className="op-card__body">
              <div className="op-card__tag">📲 Scan &amp; Order</div>
              <h3 className="op-card__title">Zero-wait digital menu for every table</h3>
              <p className="op-card__desc">No printed menus, no waiting for staff. Customers scan the table QR, browse an always-updated digital menu, and order in seconds.</p>
            </div>
          </div>

          {/* Full – Online + User rights */}
          <div className="op-card op-card--full" style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            <div className="op-card__body" style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <div className="op-card__tag">🔐 User Access Control</div>
              <h3 className="op-card__title">Every staff member sees only what they need to</h3>
              <p className="op-card__desc">
                Counter cashiers, kitchen staff, floor managers, and owners all get separate logins with exactly the right permissions — no more, no less.
              </p>
              <ul className="op-card__list">
                <li>Role-based access: cashier, manager, owner, kitchen</li>
                <li>Discount &amp; void requires manager PIN</li>
                <li>Counter-wise cash register reconciliation</li>
                <li>Shift-wise staff handover with full audit trail</li>
              </ul>
            </div>
            <img src={userImg} alt="User Rights" className="op-card__img" style={{ height: "100%", minHeight: 260 }} />
          </div>
        </div>
      </section>

      {/* ── DEEP DIVE ── */}
      <section className="op-deepdive">
        <div className="op-deepdive__inner">
          <div className="op-bento__header">
            <div className="op-section-eyebrow">Online &amp; Loyalty</div>
            <h2 className="op-section-title">Bring the food court experience online</h2>
          </div>

          <div className="op-deepdive__grid">
            <div className="op-deepdive__img-wrap">
              <img src={scanImg} alt="Online Ordering" />
              <div className="op-deepdive__img-badge">🌐 Online Ordering</div>
            </div>
            <div className="op-deepdive__text">
              <h3>Your food court's own website ordering page</h3>
              <p>
                Let customers pre-order from your food court's branded website. Office workers order lunch during morning breaks — no queues, no waiting, just pick up and go.
              </p>
              <ul className="op-check-list">
                {[
                  "Branded ordering page — your food court's identity",
                  "Multi-vendor cart: one order from multiple counters",
                  "Slot-based pre-order with pickup time selection",
                  "Online payment via UPI, card, or wallets",
                  "Order confirmation and ready-alert via SMS"
                ].map((t, i) => (
                  <li key={i}>
                    <span className="op-check-icon"><CheckCircle size={13} /></span> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="op-deepdive__grid op-deepdive__grid--reverse" style={{ marginTop: 60 }}>
            <div className="op-deepdive__img-wrap">
              <img src={loyaltyImg} alt="Loyalty" />
              <div className="op-deepdive__img-badge">🎁 Loyalty</div>
            </div>
            <div className="op-deepdive__text">
              <h3>Food court loyalty — points across all counters</h3>
              <p>
                Customers earn points whether they eat at the burger counter or the juice bar. Redeem anywhere in the food court. One wallet — the whole court benefits.
              </p>
              <ul className="op-check-list">
                {[
                  "Unified loyalty wallet across all counters",
                  "Points auto-award on every bill",
                  "Redeem at any counter in the court",
                  "Special member-only weekday offers",
                  "Corporate canteen meal card / prepaid wallet support"
                ].map((t, i) => (
                  <li key={i}>
                    <span className="op-check-icon"><CheckCircle size={13} /></span> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── MINI STRIP ── */}
      <section className="op-strip">
        <div className="op-strip__grid">
          {[
            { icon: "🎫", title: "Token Display", desc: "Order token display screen at pickup point — zero confusion for customers." },
            { icon: "💼", title: "Corporate Canteen", desc: "Employee meal cards, monthly credit limits, and department-wise billing for corporate cafeterias." },
            { icon: "🧾", title: "Consolidated Bills", desc: "One receipt for multi-counter orders — cleaner customer experience." },
            { icon: "📈", title: "Vendor Analytics", desc: "Show each vendor their own sales data — peak hours, bestsellers, daily revenue." },
          ].map((f, i) => (
            <div className="op-mini-card" key={i}>
              <div className="op-mini-card__icon">{f.icon}</div>
              <div className="op-mini-card__title">{f.title}</div>
              <p className="op-mini-card__desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="op-cta" id="op-demo">
        <div className="op-cta__inner">
          <div className="op-cta__left">
            <h2>Manage every counter, vendor &amp; order from one screen</h2>
            <p>Book a free demo. We'll show you QR ordering, counter reports, and the owner dashboard live — and set it all up for you on Day 1.</p>
            <div className="op-cta__form">
              <input className="op-cta__input" placeholder="Your name *" />
              <div className="op-cta__row">
                <input className="op-cta__input" placeholder="Phone number *" />
                <input className="op-cta__input" placeholder="City *" />
              </div>
              <input className="op-cta__input" placeholder="Food court / canteen name *" />
              <button className="op-cta__submit">Book Free Demo →</button>
            </div>
          </div>
          <div className="op-cta__right-img">
            <img src={demoImg} alt="Demo" />
          </div>
        </div>
      </section>
    </div>
  );
}
